import { useEffect, useRef } from 'react';
import { Animated, Keyboard, Platform } from 'react-native';

/**
 * Keeps form content above the keyboard: collapses the header image and pads the bottom by the keyboard
 * height so the submit button scrolls into view.
 */
export default function useKeyboardFormAnimation(initialImageHeight = 200) {
	const keyboardHeight = useRef(new Animated.Value(0)).current;
	const imageHeight = useRef(new Animated.Value(initialImageHeight)).current;

	useEffect(() => {
		// iOS fires *Will* events (smoother, in sync with the keyboard); Android only *Did*.
		const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
		const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

		const animate = (toKeyboard: number, toImage: number, duration: number) =>
			Animated.parallel([
				Animated.timing(keyboardHeight, { toValue: toKeyboard, duration, useNativeDriver: false }),
				Animated.timing(imageHeight, { toValue: toImage, duration, useNativeDriver: false }),
			]).start();

		const showSub = Keyboard.addListener(showEvent, (e) => animate(e.endCoordinates.height, 0, e.duration || 250));
		const hideSub = Keyboard.addListener(hideEvent, (e) => animate(0, initialImageHeight, e.duration || 250));

		return () => {
			showSub.remove();
			hideSub.remove();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { keyboardHeight, imageHeight };
}
